import { DateTime } from 'luxon';
import type { Reservation } from '@restaurant/api-client';
import { ArrivalStatus, ReservationStatus } from '@restaurant/types';

export type DisplayStatus =
  | 'pending'
  | 'confirmed'
  | 'rejected'
  | 'cancelled'
  | 'rescheduled'
  | 'waiting'
  | 'arrived'
  | 'seated'
  | 'in-progress'
  | 'completed'
  | 'no-show';

export type DateContext = 'future' | 'today' | 'past';

/**
 * Classify a UTC ISO timestamp relative to "today" in the restaurant's local
 * timezone (Settings.defaultZone). Pass reservation.time directly - never slice.
 */
export function getDateContext(utcIso: string): DateContext {
  const dt = DateTime.fromISO(utcIso, { zone: 'utc' }).toLocal();
  const today = DateTime.now().startOf('day');
  if (dt >= today && dt < today.plus({ days: 1 })) return 'today';
  if (dt < today) return 'past';
  return 'future';
}

export function getDisplayStatus(
  status: Reservation['status'],
  arrivalStatus: Reservation['arrivalStatus'],
): DisplayStatus {
  if (status === ReservationStatus.PENDING) return 'pending';
  if (status === ReservationStatus.REJECTED) return 'rejected';
  if (status === ReservationStatus.CANCELLED) return 'cancelled';

  if (arrivalStatus === ArrivalStatus.ARRIVED) return 'arrived';
  if (arrivalStatus === ArrivalStatus.SEATED) return 'seated';
  if (arrivalStatus === ArrivalStatus.IN_PROGRESS) return 'in-progress';
  if (arrivalStatus === ArrivalStatus.COMPLETED) return 'completed';
  if (arrivalStatus === ArrivalStatus.NO_SHOW) return 'no-show';
  if (arrivalStatus === ArrivalStatus.RESCHEDULED) return 'rescheduled';

  return 'waiting'; // CONFIRMED + WAITING or null
}

/**
 * Resolve the badge label for a display status. `labels` is the localized
 * DisplayStatus→text map (t.reservations.displayStatus). A future
 * confirmed-but-not-arrived booking reads as "Confirmed" rather than "Waiting".
 */
export function getDisplayStatusLabel(
  displayStatus: DisplayStatus,
  dateCtx: DateContext,
  labels: Record<string, string>,
): string {
  if (displayStatus === 'waiting' && dateCtx === 'future') return labels.confirmed;
  return labels[displayStatus] ?? displayStatus;
}

export function getStatusColor(displayStatus: DisplayStatus): string {
  const colors: Record<DisplayStatus, string> = {
    pending: '#ff9500',
    confirmed: '#007aff',
    rejected: '#ff3b30',
    cancelled: '#8e8e93',
    rescheduled: '#00c7be',
    waiting: '#34c759',
    arrived: '#5856d6',
    seated: '#30b0c7',
    'in-progress': '#bf5af2',
    completed: '#8e8e93',
    'no-show': '#ff9500',
  };
  return colors[displayStatus];
}

export function isTerminalStatus(
  status: ReservationStatus,
  arrivalStatus: ArrivalStatus | null,
): boolean {
  return (
    status === ReservationStatus.REJECTED ||
    status === ReservationStatus.CANCELLED ||
    arrivalStatus === ArrivalStatus.COMPLETED ||
    arrivalStatus === ArrivalStatus.NO_SHOW
  );
}
